import { LocaleData } from '../../../models/locale/locale-data';
import { LandingVariantCopy } from '../../../models/landing/variant';

// New-UX (`-v1`) shared copy — the approved redesign strings (mockup pack
// round 3, 26-Aug-2026 product feedback). The three `-v1` variant keys reuse
// this block; ` **` footnote markers on the CTA headings are appended by the
// components, not stored here.
const v2Copy: LandingVariantCopy = {
  banner: 'ENREGISTREZ-VOUS MAINTENANT',
  heroCopy: 'Félicitation pour votre achat !',
  heroSubCopy1:
    'Enregistrez votre appareil dans les 60 jours pour débloquer vos avantages gratuits',
  benefitTicks: [
    'Garantie gratuite de 10 ans sur les pièces détachées*',
    'Pièces d’origine Beko',
    'Protection supplémentaire optionnelle contre les dommages accidentels et les pannes',
  ],
  heroDisclaimer:
    'La garantie couvre les pièces telles que les moteurs, les pompes, les cartes de commande et autres composants éligibles.',
  registration: 'Activez vos avantages en un appel',
  ctaPhoneText: 'Appeler le 028 989 961',
  ctaPhoneLink: 'tel:+3228989961',
  phoneSubCopy: 'Lignes ouvertes du lundi au vendredi de 8h00 à 18h00.',
  onlineCopy: 'Enregistrez-vous en ligne en quelques minutes',
  ctaOnlineText: 'S’enregistrer en ligne',
  sixtyDayHeader: 'Enregistrez-vous dans les 60 jours',
  sixtyDayBody:
    'Activez votre garantie gratuite de 10 ans sur les pièces détachées avant la fin de votre période d’enregistrement',
};

// LP1 hybrid (`phone-only-v2`, Sept 2026) — LP1 COPY V2_SEPT 2026 2.xlsx (BEKO tab)
// as applied in the approved mockups (claude-notes/lp-hybrid-lp1). Every line
// matches Beko control or V1. Dials the same number as V1: `ctaPhoneLink` is
// V1's (undefined = the base link). The `**` on the first heading is appended
// by PhoneOnlyV2.
const lp1HybridCopy: LandingVariantCopy = {
  banner: 'Enregistrez-vous maintenant',
  offerHeadline: ['10 ANS OFFERTS', 'sur la garantie pièces détachées*'],
  sixtyDayHeader:
    'Enregistrement nécessaire sous 60 jours suivant la date d’achat de votre appareil',
  registration: 'Appelez maintenant pour activer',
  ctaPhoneText: 'Appeler le 028 989 961',
  ctaPhoneLink: v2Copy.ctaPhoneLink,
  benefitsHeader: 'Débloquez maintenant vos avantages gratuits:',
  benefitTicks: [
    'Garantie gratuite de 10 ans sur les pièces détachées',
    'Pièces d’origine Beko',
    'Protection supplémentaire optionnelle contre les dommages accidentels et les pannes',
  ],
  phoneSubCopy: 'Lignes ouvertes du lundi au vendredi de 8h00 à 18h00.',
  repeatHeading: 'Prêt à enregistrer votre appareil ?',
  ctaPhoneRepeatText: 'Appeler le 028 989 961',
};

export const data: LocaleData = {
  banner: 'Activez votre garantie GRATUITE de ',
  heroNumber: '10',
  heroCopy: 'ans de garantie sur les pièces détachées*',
  heroSubCopy1: '<b>Activation nécessaire</b>',
  heroSubCopy2: 'sous 60 jours suivant la date d’achat de votre appareil',
  registration: 'Appelez et activez dès maintenant**',
  ctaPhoneText: '028 989 961',
  ctaPhoneLink: 'tel:028989961',
  phoneSubCopy:
    "L'activation par téléphone est rapide et vous fournit des informations complémentaires sur vos garanties constructeur et pièces détachées de 10 ans. La hotline d'activation est ouverte du lundi au vendredi, de 8:00 à 18:00.",
  onlineCopy: '',
  ctaOnlineText: '',
  ctaOnlineLink: 'https://beko.register-appliance.com/RegistroPorMarca?country=BE&language=fr',
  onlineJourneyLaunch: 'Live',
  variants: {
    'phone-only-v1': v2Copy,
    'phone-and-link-v1': v2Copy,
    'phone-only-v2': lp1HybridCopy,
    'link-only-v1': {
      ...v2Copy,
      ctaOnlineText: 'Enregistrez-vous',
      linesClosedMessage:
        'Nos lignes téléphoniques sont actuellement fermées. Vous pouvez toutefois enregistrer votre appareil en ligne à tout moment.',
      phoneNumberAsterix: [
        '** Domestic & General Insurance Europe AG enregistrera votre appareil électroménager. Domestic & General vous proposera des offres d’assurance pour la protection de vos appareils électroménagers, si cela vous intéresse. Pour obtenir des informations sur le traitement de vos données par Beko Belgium NV et Domestic & General Insurance Europe AG., rendez-vous sur <0>www.domesticandgeneral.com/beko</0>',
      ],
      phoneNumberAsterixLinks: ['https://www.domesticandgeneral.com/beko'],
    },
    // LP3 COPY V1_JULY 2026.xlsx (BEKO tab; ** added per master sheet).
    'link-only': {
      registration:
        'Enregistrez dès maintenant votre appareil pour activer vos avantages gratuits**',
      onlineCopy:
        'Nos lignes téléphoniques sont actuellement fermées. Vous pouvez toutefois enregistrer votre appareil en ligne à tout moment.',
      ctaOnlineText: 'Enregistrez-vous',
      phoneNumberAsterix: [
        '** Domestic & General Insurance Europe AG enregistrera votre appareil électroménager. Domestic & General vous proposera des offres d’assurance pour la protection de vos appareils électroménagers, si cela vous intéresse. Pour obtenir des informations sur le traitement de vos données par Beko Belgium NV et Domestic & General Insurance Europe AG., rendez-vous sur <0>www.domesticandgeneral.com/beko</0>',
      ],
      phoneNumberAsterixLinks: ['https://www.domesticandgeneral.com/beko'],
    },
  },
  benefitsHeader: "Vos avantages d'activation:",
  benefits: [
    'Garantie pièces gratuites pendant 10 ans',
    'Conservation de votre preuve d’achat au format numérique',
    'Mises à jour importantes sur la sécurité de l’appareil',
  ],
  footerLinks: [
    { text: 'Information légale', link: '/terms' },
    { text: 'Politique de confidentialité', link: 'https://www.domesticandgeneral.com/beko' },
    { text: 'Cookies', link: '/cookies' },
  ],
  footerCopy:
    "Ce site web appartient à Domestic & General Group Limited et est exploité par Domestic & General Insurance Europe AG, Hagenauer Straße 44, 65203 Wiesbaden, Allemagne. Domestic & General Insurance Europe AG est agréée et réglementée par l'Autorité fédérale de surveillance financière (BaFin) en Allemagne.",
  heroAsterix: [
    '* Enregistrez votre appareil dans un délai de 60 jours à compter de la date d’achat afin d’étendre de 10 ans la garantie offerte sur les pièces détachées de votre appareil (Soit une durée de 12 ans période de garantie légale incluse). En cas de panne à l’expiration de la garantie légale, nous vous proposerons gratuitement le remplacement des pièces défectueuses (les coûts de déplacement et de main-d’œuvre restant à votre charge). Pour que l’extension de la garantie de 10 ans sur les pièces détachées soit valide, les réparations doivent être effectuées par le service après-vente officiel du groupe Beko. Offre soumise à conditions et seulement valable en Belgique. Pour plus d’informations: <0>https://www.beko.com/be-fr</0>',
  ],
  heroAsterixLinks: ['https://www.beko.com/be-fr'],
  phoneNumberAsterix: [
    '** Il s’agit d’un numéro de téléphone fixe détenu par Domestic & General Insurance Europe AG. Les tarifs d’appel habituels de votre opéateur téléphonique s’appliquent. Domestic & General vous proposera des offres d’assurance pour la protection de vos appareils électroménagers, si cela vous intéresse. Pour obtenir des informations sur le traitement de vos données par Beko Belgium NV et Domestic & General Insurance Europe AG., rendez-vous sur <0>https://www.domesticandgeneral.com/beko</0>',
  ],
  phoneNumberAsterixLinks: ['https://www.domesticandgeneral.com/beko'],
  additionalSmallPrint: '',
  languageSwitcherLabel: 'Pays',
  languageSwitcherPlaceholder: 'Sélectionnez votre pays',
  languageList: [
    { label: 'Allemagne', value: 'de-DE' },
    { label: 'France', value: 'fr-FR' },
    { label: 'Italie', value: 'it-IT' },
    { label: 'Espagne', value: 'es-ES' },
    { label: 'Pays-Bas', value: 'nl-NL' },
    { label: 'Belgique (français)', value: 'be-FR' },
    { label: 'Belgique (néerlandais)', value: 'be-NL' },
  ],
  cookiePolicy: {
    title: 'Que sont les cookies ?',
    sections: [
      {
        heading: 'Que sont les cookies ?',
        description: [
          "Les cookies sont de petits fichiers texte envoyés sur votre appareil lorsque vous visitez un site web. Ils restent sur votre appareil et sont renvoyés au site web d'origine à chaque nouvelle visite. Les cookies collectent des données vous concernant, telles que votre langue préférée ou vos informations de connexion. Ces cookies sont installés par nos soins et sont appelés cookies propriétaires. Nous utilisons également des cookies tiers, qui proviennent d'un domaine différent de celui du site web que vous consultez. Ils peuvent nous aider à améliorer nos publicités et notre marketing. Nous utilisons des cookies et d'autres technologies de suivi aux fins suivantes:",
        ],
      },
      {
        heading: 'Gestion des cookies',
        description: [
          "Pour modifier vos paramètres de cookies, utilisez notre outil de gestion des cookies ou l'icône de sécurité présente sur chaque page. Vous pouvez bloquer l'utilisation de tout ou partie des cookies et technologies similaires que nous utilisons sur le site web en cliquant sur les différentes catégories de cookies. Cependant, sachez que cela peut affecter votre expérience sur le site web et les services que nous proposons. Sachez également que la suppression de tous les cookies de votre navigateur supprimera tous les cookies stockant vos préférences.",
          "La plupart des navigateurs vous indiqueront comment refuser les nouveaux cookies, être averti de la réception d'un nouveau cookie et désactiver complètement les cookies. Pour supprimer les cookies déjà installés sur votre navigateur, vous pouvez sélectionner l'option « Effacer votre historique de navigation » et choisir de supprimer ou d'effacer les cookies.",
          'Certains navigateurs, tels que Chrome et Firefox, vous permettent de modifier vos paramètres pour naviguer en mode « navigation privée », limitant ainsi la quantité de données placées sur votre machine et supprimant automatiquement tous les cookies persistants placés sur votre appareil lorsque vous terminez votre session de navigation.',
          'Vous pouvez également consulter le site www.aboutcookies.org qui contient des informations complètes sur la manière de supprimer les cookies sur une grande variété de navigateurs.',
          "Pour en savoir plus sur les cookies en général et savoir quels tiers ont activé les cookies sur votre appareil mobile, et comment les désactiver, consultez www.networkadvertising.org/mobile-choices . Sur votre appareil mobile, vous pouvez également sélectionner « Limiter le suivi publicitaire » (sur iOS) ou « Désactiver les publicités ciblées par centres d'intérêt » (sur Android), ce qui vous permet de limiter l'utilisation des informations relatives à votre utilisation des applications pour diffuser des publicités ciblées.",
        ],
      },
      {
        heading: 'Sites Web tiers',
        description: [
          "Le site web peut contenir des liens ou être redirigé vers d'autres sites web. En cliquant sur le site web lié, vous quittez le site et son contenu sous notre contrôle et êtes redirigé vers le site web tiers. Nous n'avons aucun contrôle sur le contenu du site web lié et déclinons toute responsabilité quant aux informations et au fonctionnement de ce dernier. Cependant, nous supprimerons le lien vers le site web lié dès que nous aurons connaissance de la présence de contenu illégal ou autre sur le site web lié. Les opérateurs du site web lié peuvent collecter des informations vous concernant et les utiliser conformément à leur politique de confidentialité, qui peut différer de la nôtre. Nous ne contrôlons pas la configuration des cookies sur le site web lié ; nous vous suggérons donc de consulter les sites web liés pour plus d'informations sur leurs cookies et leur gestion.",
        ],
      },
      {
        heading: 'Sécurité',
        description: [
          'Nous nous engageons à assurer la sécurité de notre site web et à offrir une interface client exempte de fraude. Toutes les informations échangées sont cryptées grâce au logiciel SSL et nous avons tout mis en œuvre pour protéger vos informations personnelles contre tout accès non autorisé.',
        ],
      },
    ],
  },
  termsAndConditions: {
    title: 'Information légale',
    content: [
      {
        heading: 'Les conditions générales du site',
      },
      {
        heading: 'Définitions',
        paragraphs: [
          "« D&G » signifie Domestic & General Insurance Europe AG, Hagenauer Straße 44, 65203 Wiesbaden, Allemagne. numéro de registre commerciale HRB 30859. Domestic & General Insurance Europe AG est autorisée par et réglementée par Bundesanstalt für Finanzdienstleistungsaufsicht (BaFin) de l'Allemange. Le numéro d'immatriculation au Registre Services Financiers est 5227.",
          '« Client » désigne Beko Belgium NV',
          '« Nous » désigne Domestic & General Insurance Europe AG.',
          "« Site web » désigne le sous domaine d'inscription et de garantie du Client, disponible à l'adresse <0>https://whirlpool.register-appliance.com/RegistroPorMarca?country=BE&language=fr</0>  il est la propriété du D&G.",
          "« Vous » désigne l'utilisateur de ce site web.",
          "Informations sur l'entreprise",
          'Président du conseil de surveillance: David Tyler',
          "Conseil d'administration: Matthew Crummack (President), Thomas Goode, Matthias Kammermann, Sven Wick",
          "Numéro d'identification fiscal: 014 223 10798",
          'Numéro de TVA: DE322598066',
          "Cour d'enregistrement: Wiesbaden, Allemagne.",
        ],
        links: ['https://whirlpool.register-appliance.com/RegistroPorMarca?country=BE&language=fr'],
      },
      {
        heading: 'Utilisation du site web',
        paragraphs: [
          "Vous avez l'autorisation de naviguer dans ce site web et de reproduire des extraits par impression ou téléchargement sur un disque dur à des fins d'usage personnel uniquement. Vous ne pouvez pas:",
          "Copier, distribuer, modifier ni altérer d'aucune manière, ni utiliser autrement tout contenu de ce site, à l'exception des cas permis ci-dessus. Copier, distribuer ni autrement utiliser tout contenu de ce site à des fins commerciales.",
          "Retirer toute mention de copyright, marque déposée ou autre mention de propriété intellectuelle figurant à l'origine dans le contenu copié ou imprimé à partir du site web.",
          'Veuillez noter que tous les droits de copyright et de propriété intellectuelle propres aux contenus de ce site web sont la propriété de D&G.',
          "Le site web contient des marques de commerce, notamment le nom et le logo du Client qui appartiennent au Client, et le nom et le logo de D&G, qui appartiennent à D&G. Aucun logo ni aucune image du site ne peut être retransmise ni copiée, partiellement ou en intégralité, sans l'autorisation expresse de son propriétaire. Vous trouverez plus d'informations sur la façon dont les données personnelles recueillies sur ce site web seront utilisées dans la Déclaration de sécurité et de confidentialité des données sur ce site.",
        ],
      },
      {
        heading: 'Contenu',
        paragraphs: [
          "Toutes les informations contenues dans ce site sont susceptibles d'être modifiées sans préavis. Nous ne nous engageons pas à maintenir les informations à jour en permanence. Nous avons pris des mesures raisonnables pour vérifier l'exactitude et la pertinence du contenu de ce site, mais nous ne faisons aucune garantie – explicite ou implicite – ni aucune représentation concernant le contenu de ce site, y compris concernant la valeur commerciale, l'adéquation à un usage particulier, le titre et la légalité. Toutes les informations sont fournies « telles quelles » et nous ne pouvons être tenus pour responsables en cas de perte ou de dommages résultats de l'utilisation des informations ou des conseils contenus dans ce site ; nous ne pouvons pas non plus être tenus pour responsables en cas d'erreur ou d'omission dans le contenu de ce site. De plus, nous ne pouvons garantir que le site web sera disponible de façon ininterrompue, et nous déclinons toute responsabilité en cas de perte ou de dommage faisant suite à une telle indisponibilité.",
        ],
      },
      {
        heading: 'Liens vers des sites externes',
        paragraphs: [
          "Ce site peut inclure des liens vers des sites externes. Ces liens vous sont fournis pour vous aider à trouver rapidement et facilement des informations supplémentaires. Lorsque vous cliquez sur un lien, vous pouvez quitter notre site et le contenu contrôlé par D&G et être redirigé vers le site d'un tiers externe (« site web lié »). D&G n'a aucune influence sur le contenu du site web lié et n'est pas responsable des informations fournies sur le site web lié. Il n'est pas raisonnablement possible pour D&G de surveiller constamment le contenu des sites liés sans indication spécifique d'infraction. Toutefois, D&G supprimera le lien vers un site si la présence de contenu illégal ou autrement délictueux sur le site web lié est portée à sa connaissance. Nous déclinons toute responsabilité vis-à-vis du contenu et de l'utilisation de ces sites liés.",
        ],
      },
      {
        heading: 'Qui sommes-nous',
        paragraphs: ['Le site web est géré pour le compte du Client par D&G.'],
      },
      {
        heading: "Services d'enregistrement de produits",
        paragraphs: [
          "Domestic & General Insurance Europe AG fournit des services d'enregistrement de produit pour le compte du Client.",
        ],
      },
      {
        heading: 'Informations importantes au sujet du fournisseur',
        paragraphs: [
          "L'offre et la conclusion de contrats d'assurances sur ce site web sont conduites par Domestic & General Insurance Europe AG. Tous les contrats d'assurance achetés sur ce site web sont soumis aux conditions générales des polices qui régissent leur utilisation. Vous devez lire les conditions générales des polices ainsi que toutes les autres conditions fournies sur ce site web. Domestic & General Insurance Europe AG est une société immatriculée en Allemagne sous le numéro de registre commerciale HRB 30859, ayant son siège social à Hagenauer Str. 44, 65203 Wiesbaden, Allemagne. Domestic & General Insurance Europe AG est contrôlée et réglementée par la Bundesanstalt für Finanzdienstleistungsaufsicht (BaFin) de l’Allemagne sous le numéro d'enregistrement 5227. Vous pouvez consulter les registres BAFIN sur <0>https://www.bafin.de/EN/PublikationenDaten/Datenbanken/Unternehmenssuche/unternehmenssuche_node_en.html</0>.",
        ],
        links: [
          'https://www.bafin.de/EN/PublikationenDaten/Datenbanken/Unternehmenssuche/unternehmenssuche_node_en.html',
        ],
      },
      {
        heading: 'Autorité compétente:',
        paragraphs: [
          'Bundesanstalt für Finanzdienstleistungsaufsicht (BaFin)',
          'Graurheindorfer Straße 108',
          '53117 Bonn',
          'Tel: +49 (0)228 410 0',
          'Fax: +49 (0)228 410 81550',
          "Dans le cadre de la troisième directive « assurance non-vie », Domestic & General Insurance Europe AG est autorisée à exercer ses activités d'assurance par la mise en place d'une succursale (« Passeport d'installation dans l'EEE ») dans les états-membres suivants de l’Espace économique européen : France, Italie et Espagne. D&G est également autorisé à poursuivre des services d'assurance à distance à partir de l'une des hubs mentionnées ci-dessus (« Passeport des services dans l'EEE ») dans les états-membres suivants de l’Espace économique européen : Autriche, Belgique, Irlande, Pologne, Portugal et les Pays-Bas.",
          "Domestic & General Insurance Europe AG exerce des activités d'assurance en Belgique en libre prestation de services et est enregistrée comme compagnie d'assurance auprès de la Banque nationale de Belgique sous le numéro 3156 pour exercer des activités d'assurance en Belgique en libre prestation de services (www.nbb.be).",
          'Banque nationale de Belgique (NBB)',
          'de Berlaimontlaan 3',
          '1000 Brussel',
          'Tel: +32 (0)2 221 21 11',
        ],
      },
      {
        heading: 'Responsabilité',
        paragraphs: [
          "Nous déclinons toute responsabilité en cas de panne, de dysfonctionnement ou d'indisponibilité de ce site web, à moins que cela ne résulte de notre défaillance volontaire, d'une fraude ou à une négligence grave de notre part. Nous déclinons toute responsabilité vis-à-vis des dommages et pertes spéciaux, indirects ou accidentels subis par vous (y compris toute perte de profits, d'opportunité ou de plus-value) suite à l'utilisation de ce site web ou à son dysfonctionnement. Rien dans ces conditions générales n'exclut ni ne limite notre responsabilité en cas de décès ou de dommage corporel résultant de notre négligence. Nous pouvons suspendre, retirer, limiter ou annuler l'utilisation de ce site web à tout moment.",
        ],
      },
      {
        heading: 'Droit applicable',
        paragraphs: [
          'Ces conditions générales et votre utilisation du site web sont régies par le droit belge.',
        ],
      },
      {
        heading: 'Limites territoriales',
        paragraphs: [
          "Le site web est actuellement destiné aux personnes qui le consultent et l'utilisent depuis la Belgique.",
        ],
      },
      {
        heading: 'Virus',
        paragraphs: [
          "Nous prenons des mesures raisonnables pour veiller à ce que ce site soit exempt de virus. Toutefois, nous ne pouvons être tenus pour responsables d'aucun dommage causé par un virus ou autre technologie suite à votre utilisation de ce site, ou au téléchargement de son contenu ou du contenu d'un site lié.",
        ],
      },
      {
        heading: 'Conseils et recommandations',
        paragraphs: [
          "Rien dans ce site web ne doit être considéré comme un conseil ou une recommandation. Afin de décider d'acheter ou non un contrat d'assurance sur ce site, vous ne devez pas vous appuyer uniquement sur les informations contenues dans ses pages. Vous devez lire attentivement la documentation des polices et/ou les conditions générales propres à un produit spécifique avant de prendre une décision d'achat.",
        ],
      },
      {
        heading: 'Nous contacter',
        paragraphs: [
          'Si vous avez des préoccupations concernant ce site web ou si vous souhaitez nous contacter comme suit:',
          'Tel: +49 (0)611 308 78 0',
          'Fax: +49 (0)611 308 78 55',
          'Email: servicebe@domesticandgeneral.com.',
          'Si vous souhaitez contacter l’ombudsman des assurances, vous pouvez le faire de la manière suivante :',
          'Ombudsman des Assurances',
          'de Meeûssquare 35',
          '1000 Brussel',
          'Tel: 02 547 58 71',
          'E-mail: info@ombudsman.as',
          "Merci d'avoir visité ce site. Veuillez noter que les conditions générales ci-dessus peuvent être modifiées par D&G de temps à autres et nous vous recommandons donc de les vérifier à chaque fois que vous visitez ce site.",
        ],
      },
    ],
  },
};
